import {
    Box,
    ClickAwayListener,
    IconButton,
    Typography,
    styled
} from "@mui/material";
import {
    Dispatch,
    ReactNode,
    SetStateAction,
    useEffect,
    useRef,
    useState
} from "react";

import EditIcon from "../icons/EditIcon";
import _ from "lodash";

export type FieldDisplayData<T> = {
    label?: string;
    data: T;
};

// For strings, allow readView to be optional
export type ReadonlyFieldDisplayProps<T> = T extends string ? (FieldDisplayData<T> & {
    readView?: ReactNode | ((props: { data: T; }) => ReactNode);
}) : (FieldDisplayData<T> & {
    readView: ReactNode | ((props: { data: T; }) => ReactNode);
});

export type EditableFieldDisplayProps<T> = ReadonlyFieldDisplayProps<T> & {
    editView: (props: { data: T; setData: Dispatch<SetStateAction<T>>; }) => ReactNode;
    onEditSubmit: (data: T) => (void | Promise<void>);
};

export type FieldDisplayProps<T> = ReadonlyFieldDisplayProps<T> | EditableFieldDisplayProps<T>;

export const FieldDisplayLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.body2.fontSize,
    fontWeight: 500,
}));

export const FieldDisplayValue = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.body2.fontSize,
    fontWeight: theme.typography.body2.fontWeight,
    whiteSpace: "pre-line",
}));

export const FieldDisplay = <T,>(props: FieldDisplayProps<T>) => {
    const { label, readView, data: data } = props;
    const isSavingRef = useRef<boolean>(false);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [dataDraft, setDataDraft] = useState<T>(data);

    useEffect(() => {
        setDataDraft(data);
    }, [data]);

    const isEditable = "onEditSubmit" in props &&
        "editView" in props
        && (props as EditableFieldDisplayProps<T>).onEditSubmit
        && (props as EditableFieldDisplayProps<T>).editView;

    if (!isEditable) {
        return (
            <Box
                display="flex"
                gap={1}
                py={0.25}
                flex={1}
            >
                {label && <FieldDisplayLabel>{label}:</FieldDisplayLabel>}
                {!readView && typeof data === "string" && (
                    <FieldDisplayValue>{data}</FieldDisplayValue>
                )}
                {readView && (
                    typeof readView === "string" ? (
                        <FieldDisplayValue>{readView}</FieldDisplayValue>
                    ) : typeof readView === "function" ? (
                        readView({ data })
                    ) : (
                        readView
                    )
                )}
            </Box>
        );
    }

    const { editView, onEditSubmit } = props as EditableFieldDisplayProps<T>;

    return (
        <ClickAwayListener
            onClickAway={async () => {
                setIsEditing(false);
                if (!isEditing || isSavingRef.current) {
                    return;
                }
                if (_.isEqual(dataDraft, data)) {
                    return;
                }
                isSavingRef.current = true;
                await onEditSubmit(dataDraft);
                isSavingRef.current = false;
            }}
        >
            <Box
                display="flex"
                gap={1}
                onClick={() => setIsEditing(true)}
                sx={{
                    transition: "background-color 0.3s",
                    ":hover": {
                        backgroundColor: !isEditing ? theme => theme.palette.action.hover : undefined,
                        "& .MuiIconButton-root": {
                            opacity: !isEditing ? 1 : 0,
                            transform: !isEditing ? "translateX(0px)" : "translateX(10px)"
                        }
                    },
                    "& .MuiIconButton-root": {
                        opacity: 0,
                        transform: "translateX(10px)",
                        transition: "opacity 0.3s, transform 0.3s"
                    }
                }}
                py={0.25}
                flex={1}
                alignItems="center"
            >
                {label && <FieldDisplayLabel>{label}:</FieldDisplayLabel>}
                {isEditing ? editView({ data: dataDraft, setData: setDataDraft }) : (
                    <>
                        {!readView && typeof data === "string" && (
                            <FieldDisplayValue>{data}</FieldDisplayValue>
                        )}
                        {readView && (
                            typeof readView === "string" ? (
                                <FieldDisplayValue>{readView}</FieldDisplayValue>
                            ) : typeof readView === "function" ? (
                                readView({ data })
                            ) : (
                                readView
                            )
                        )}
                        <IconButton size="small" onClick={() => setIsEditing(true)} sx={{ width: "1.25rem", height: "1.25rem" }}>
                            <EditIcon sx={{ width: "1.25rem", height: "1.25rem" }} />
                        </IconButton>
                    </>
                )}
            </Box>
        </ClickAwayListener>
    );
};